const legal = {
  infoPageLayout: {
    manageCookies: "Manage my cookies",
  },
  terms: {
    title: "Terms of use",
    subtitle: "General rules applicable to access and use of Qubiq.",
    sections: [
      {
        title: "1. Purpose",
        body: "{{brandName}} is an interactive video streaming platform that lets users watch content, interact with built-in games, and use community features.",
      },
      {
        title: "2. Access to the service",
        body: "Access to certain Qubiq features requires creating an account. Users agree to provide accurate information, keep their credentials confidential, and not misuse the service.",
      },
      {
        title: "3. Prohibited content and behavior",
        body: "It is forbidden to publish, distribute, or relay unlawful, fraudulent, hateful, violent, misleading, unauthorized pornographic content, content infringing third-party rights, or content contrary to Qubiq moderation rules.",
      },
      {
        title: "4. Intellectual property",
        body: "Qubiq platform elements, design, trademarks, visuals, interfaces, and developments remain protected. Unless expressly authorized, they may not be copied, reproduced, extracted, or reused for commercial purposes.",
      },
      {
        title: "5. Account suspension or deletion",
        body: "Qubiq may restrict, suspend, or remove access to all or part of the service in the event of non-compliance with these terms, fraud attempts, behavior harmful to the community, or a security risk.",
      },
      {
        title: "6. Contact",
        body: "If you have any questions about these terms, you can write to {{contactEmail}}.",
      },
    ],
  },
  privacy: {
    title: "Privacy policy",
    subtitle: "How Qubiq handles personal data and how to exercise your rights.",
    sections: [
      {
        title: "Data controller",
        body: "{{companyName}}, accessible via {{websiteUrl}}, acts as the data controller for the data processed as part of the {{brandName}} platform.",
      },
      {
        title: "Data processed",
        body: "Depending on your use of the service, we may process: account identity, email, username, profile image, usage history, community interactions, consent preferences, technical browsing information, and data related to wallets or earnings when these features are used.",
      },
      {
        title: "Purposes and legal bases",
        body: "- Providing the service, video playback, games, profile, and notifications: performance of the service.\n- Securing the platform, fighting fraud, and moderating content: legitimate interest and legal obligations.\n- Handling support requests and GDPR rights: legal obligation and legitimate interest.\n- Measuring audience with Google Analytics: consent.",
      },
      {
        title: "Recipients",
        body: "Data may be accessed, as needed, by authorized Qubiq teams and technical service providers. In particular, Qubiq uses Clerk for authentication and Google Analytics for audience measurement when you accept it.",
      },
      {
        title: "Retention periods",
        body: "Retention periods vary depending on the purpose:\n- user account: for the duration of the account and then according to applicable legal obligations;\n- technical and security logs: the limited period necessary for security and diagnosis;\n- cookie preferences: 6 months;\n- audience data: according to the Google Analytics configuration and the retention policy selected by Qubiq.",
      },
      {
        title: "Transfers outside the EU",
        body: "Some providers may process data outside the European Union. In such cases, Qubiq implements the safeguards required by the GDPR, including appropriate contractual clauses when necessary.",
      },
      {
        title: "Your rights",
        body: "Depending on the situation, you have the rights of access, rectification, erasure, objection, restriction, portability, and the right to withdraw your consent at any time for processing based on it. You can also lodge a complaint with the CNIL.",
      },
      {
        title: "Personal data contact",
        body: "For any request relating to your personal data: {{privacyEmail}}.",
      },
    ],
  },
  legalNotice: {
    title: "Legal notice",
    subtitle: "Information about the service publisher and website hosting.",
    items: {
      editorLabel: "Website publisher",
      legalFormLabel: "Legal form",
      legalFormValue: "{{legalForm}} — share capital: {{capital}}",
      registrationLabel: "Registration",
      registrationValue: "Trade and Companies Register of {{registrationCity}} {{registrationNumber}}",
      registrationDateLabel: "Registration date",
      euidLabel: "EUID",
      vatLabel: "VAT number",
      addressLabel: "Address",
      activitiesLabel: "Main activities",
      publicationDirectorLabel: "Publication director",
      contactLabel: "Contact",
      hostingLabel: "Hosting",
      hostingValue: "{{hostingCompany}}\n{{hostingAddress}}",
      intellectualPropertyLabel: "Intellectual property",
      intellectualPropertyValue:
        "The content, interfaces, trademarks, visuals, and developments of the Qubiq service are protected. Any unauthorized reproduction or exploitation is prohibited.",
    },
  },
  cookies: {
    title: "Cookie policy",
    subtitle: "Information about the trackers used by Qubiq and how to manage your choices.",
    sections: [
      {
        title: "Principle",
        body: "Qubiq uses cookies and other trackers necessary for the service to operate. Audience measurement trackers are disabled by default and are only activated after your explicit consent.",
      },
      {
        title: "Categories used",
        body: "- Necessary cookies: security, sign-in, session stability, essential operation.\n- Audience measurement: Google Analytics 4, with consent.\n- Marketing: not currently enabled on Qubiq.",
      },
      {
        title: "Durations and third parties",
        body: "- Qubiq consent preference: stored locally under the key {{consentStorageKey}} for 6 months.\n- {{analyticsProvider}}: audience measurement cookies, default duration announced by Google: up to 2 years, subject to browser limitations and the configuration selected by Qubiq.\n- Third-party provider involved in audience measurement: Google.",
      },
      {
        title: "Google Analytics",
        body: "{{analyticsProvider}} is not loaded before your consent. When you accept it, Qubiq uses audience measurement to better understand service usage, improve performance, and identify major browsing trends.",
      },
      {
        title: "Withdraw or modify consent",
        body: "You can change your choices at any time from the “Manage my cookies” button available on this page, in the website information links, and in the profile menu.",
      },
      {
        title: "Regulatory reminder",
        body: "On Qubiq, rejecting audience measurement cookies is as easy as accepting them. The core service remains accessible without enabling Google Analytics.",
      },
    ],
  },
  moderation: {
    title: "Qubiq moderation rules",
    subtitle: "The rules of the Qubiq Republic to protect the community, creators, and players.",
    sections: [
      {
        title: "1. Respect for the community",
        body: "Insults, targeted attacks, humiliation, threats, harassment campaigns, or behavior intended to harm another user, creator, or moderator are not allowed.",
      },
      {
        title: "2. Illegal or dangerous content",
        body: "The following are prohibited: glorification of crimes, terrorism, child sexual abuse content, doxxing, unauthorized sharing of personal data, scams, fraud, hacking, hate speech, or any publication contrary to the law.",
      },
      {
        title: "3. Deception, spam, and manipulation",
        body: "It is forbidden to spam, artificially manipulate audience metrics, publish fake contests, or mislead users about rewards, games, qubs, or participation conditions.",
      },
      {
        title: "4. Protection of minors",
        body: "Sexualized, shocking, humiliating, or dangerous content for minors is prohibited. Users must avoid any inappropriate staging or exploitation of vulnerable audiences.",
      },
      {
        title: "5. Games and fair play",
        body: "Any attempt to cheat, abusive automation, bypass run rules, duplicate rewards, exploit bugs, or deliberately alter the gaming experience may result in account suspension or deletion.",
      },
      {
        title: "6. Third-party rights",
        body: "You must hold the rights necessary to publish content on Qubiq. Violations of copyright, image rights, privacy, or third-party trademarks may lead to immediate moderation.",
      },
      {
        title: "7. Possible measures",
        body: "Qubiq may remove content, limit certain features, block sharing, suspend an account, remove platform-related benefits, or report facts to the authorities when justified.",
      },
      {
        title: "8. Qubiq spirit",
        body: "Qubiq is an interactive platform: users can debate, play, perform, and share. But the experience must remain healthy, fair, readable, and respectful. If content breaks that balance, it may be moderated.",
      },
    ],
  },
};

export default legal;
